export const SUPPLIER_APPLICATION_REQUIRED_KEYS = [
  'company_name',
  'country',
  'city',
];

// The EXACT set submit_supplier_verification() refuses to submit without.
// Confirmed 2026-09-16 against two independent sources: the RPC body in
// migrations 202604011050 / 202604011130, and pg_get_functiondef() read off
// production. It had been FOUR keys here, which made isVerificationComplete
// disagree with the RPC: a supplier missing company_name, country, city or
// trade_link was told his file was complete and then hit
// 'Complete the required company and verification fields before submitting.'
// trade_link in particular appeared in NEITHER list. Zero of the 24 suppliers
// on record sat in that gap when this was fixed, so no live state flipped.
// Keep this list and the RPC in step — changing one without the other is the bug.
export const SUPPLIER_VERIFICATION_REQUIRED_KEYS = [
  'company_name',
  'country',
  'city',
  'trade_link',
  'reg_number',
  'years_experience',
  'license_photo',
  'factory_photo',
];

// Trilingual labels for the eight keys above, so every surface that has to tell a
// supplier WHAT is missing names the same thing in the same words — the dashboard
// card, the verification form and the admin queue. ar / en / zh per golden rule 3.
export const SUPPLIER_VERIFICATION_FIELD_LABELS = Object.freeze({
  company_name:     { ar: 'اسم الشركة',           en: 'Company name',          zh: '公司名称' },
  country:          { ar: 'الدولة',               en: 'Country',               zh: '国家' },
  city:             { ar: 'المدينة',              en: 'City',                  zh: '城市' },
  trade_link:       { ar: 'الرابط التجاري',       en: 'Trade link',            zh: '贸易主页链接' },
  reg_number:       { ar: 'رقم السجل التجاري',    en: 'Registration number',   zh: '营业执照注册号' },
  years_experience: { ar: 'سنوات الخبرة',         en: 'Years of experience',   zh: '经营年限' },
  license_photo:    { ar: 'صورة الرخصة التجارية', en: 'Business licence',      zh: '营业执照照片' },
  factory_photo:    { ar: 'صورة المصنع',          en: 'Factory photo',         zh: '工厂照片' },
});

// What the supplier still owes, already worded in his language and in the order he
// fills them in. Presentational code should not re-derive "missing" on its own.
export function buildSupplierVerificationChecklist(profile = {}, lang = 'ar') {
  const { missingKeys, completedRequiredCount, requiredCount, progressPercent, isVerificationComplete } =
    getSupplierVerificationState(profile);
  const labelFor = (key) => {
    const row = SUPPLIER_VERIFICATION_FIELD_LABELS[key];
    return (row && (row[lang] || row.en)) || key;
  };
  return {
    missingKeys,
    missingLabels: missingKeys.map(labelFor),
    items: SUPPLIER_VERIFICATION_REQUIRED_KEYS.map((key) => ({
      key, label: labelFor(key), done: !missingKeys.includes(key),
    })),
    done: completedRequiredCount,
    total: requiredCount,
    percent: progressPercent,
    isComplete: isVerificationComplete,
  };
}

const LEGACY_REVIEW_STATUSES = ['pending', 'under_review', 'submitted', 'review'];
const LEGACY_VERIFIED_STATUSES = ['active', 'approved'];
const LEGACY_REGISTERED_STATUSES = ['draft', 'incomplete'];

export const SUPPLIER_STATUS_EQUIVALENTS = Object.freeze({
  registered: 'registered',
  verification_required: 'verification_required',
  verification_under_review: 'verification_under_review',
  verified: 'verified',
  draft: 'registered',
  incomplete: 'registered',
  pending: 'verification_under_review',
  under_review: 'verification_under_review',
  submitted: 'verification_under_review',
  review: 'verification_under_review',
  approved: 'verified',
  active: 'verified',
  rejected: 'rejected',
  disabled: 'inactive',
  inactive: 'inactive',
  suspended: 'inactive',
});

export const SUPPLIER_REVIEW_QUEUE_RAW_STATUSES = Object.freeze(['verification_under_review', ...LEGACY_REVIEW_STATUSES]);
export const SUPPLIER_PUBLIC_VISIBLE_RAW_STATUSES = Object.freeze(['verified', ...LEGACY_VERIFIED_STATUSES]);

const SUPPLIER_STAGE_LABELS = {
  application: { ar: 'مطلوب استكمال التحقق', en: 'Verification required', zh: '需要完成认证' },
  under_review: { ar: 'التحقق تحت المراجعة', en: 'Verification under review', zh: '认证审核中' },
  approved: { ar: 'موثّق', en: 'Verified', zh: '已验证' },
  rejected: { ar: 'مرفوض', en: 'Rejected', zh: '已拒绝' },
  inactive: { ar: 'موقوف مؤقتاً', en: 'Temporarily paused', zh: '暂时停用' },
};

function normalizeRawSupplierStatus(rawStatus) {
  return String(rawStatus || 'registered').trim().toLowerCase();
}

export function getSupplierReviewQueueStatuses() {
  return [...SUPPLIER_REVIEW_QUEUE_RAW_STATUSES];
}

export function getSupplierPublicVisibilityStatuses() {
  return [...SUPPLIER_PUBLIC_VISIBLE_RAW_STATUSES];
}

export function normalizeSupplierStatus(rawStatus) {
  const normalized = normalizeRawSupplierStatus(rawStatus);
  return SUPPLIER_STATUS_EQUIVALENTS[normalized] || 'registered';
}

export function getSupplierApplicationState(profile = {}) {
  const missingCoreKeys = SUPPLIER_APPLICATION_REQUIRED_KEYS.filter((key) => {
    const value = profile?.[key];
    if (typeof value === 'number') return Number.isNaN(value);
    return !value;
  });

  const hasContactMethod = Boolean(profile?.wechat || profile?.whatsapp);
  const requiredCount = SUPPLIER_APPLICATION_REQUIRED_KEYS.length;
  const completedRequiredCount = requiredCount - missingCoreKeys.length;

  return {
    applicationMissingKeys: missingCoreKeys,
    applicationMissingCount: missingCoreKeys.length,
    applicationRequiredCount: requiredCount,
    applicationCompletedRequiredCount: completedRequiredCount,
    applicationProgressPercent: Math.round((completedRequiredCount / requiredCount) * 100),
    hasContactMethod,
    isApplicationComplete: missingCoreKeys.length === 0,
  };
}

export function getSupplierVerificationState(profile = {}) {
  const missingKeys = SUPPLIER_VERIFICATION_REQUIRED_KEYS.filter((key) => {
    const value = profile?.[key];
    // years_experience is an integer and 0 is a legitimate answer; the RPC tests
    // `is null`, so 0 must count as present. Text fields the RPC trims, so a
    // whitespace-only value is missing to it and must be missing here too.
    if (typeof value === 'number') return Number.isNaN(value);
    if (typeof value === 'string') return value.trim() === '';
    return !value;
  });

  const hasContactMethod = Boolean(profile?.wechat || profile?.whatsapp);
  const hasAnyPayoutData = Boolean(
    profile?.payout_beneficiary_name
    || profile?.bank_name
    || profile?.payout_account_number
    || profile?.swift_code
    || profile?.payout_branch_name
    || profile?.payout_iban
  );
  const isPayoutComplete = Boolean(
    profile?.payout_beneficiary_name
    && profile?.bank_name
    && profile?.payout_account_number
    && profile?.swift_code
  );
  const payoutMethod = hasAnyPayoutData ? 'swift' : '';

  return {
    missingKeys,
    missingCount: missingKeys.length,
    requiredCount: SUPPLIER_VERIFICATION_REQUIRED_KEYS.length,
    completedRequiredCount: SUPPLIER_VERIFICATION_REQUIRED_KEYS.length - missingKeys.length,
    progressPercent: Math.round(((SUPPLIER_VERIFICATION_REQUIRED_KEYS.length - missingKeys.length) / SUPPLIER_VERIFICATION_REQUIRED_KEYS.length) * 100),
    hasContactMethod,
    isVerificationComplete: missingKeys.length === 0,
    payoutMethod,
    isPayoutComplete,
  };
}

export function hasConfirmedEmail(sessionUser) {
  return Boolean(sessionUser?.email_confirmed_at || sessionUser?.confirmed_at);
}

export function getSupplierResolvedStatus(profile = {}, sessionUser = null) {
  const rawStatus = normalizeRawSupplierStatus(profile?.status);
  const emailConfirmed = sessionUser ? hasConfirmedEmail(sessionUser) : null;
  const application = getSupplierApplicationState(profile);

  if (rawStatus === 'verified' || LEGACY_VERIFIED_STATUSES.includes(rawStatus)) return 'verified';
  if (rawStatus === 'rejected') return 'rejected';
  if (['disabled', 'inactive', 'suspended'].includes(rawStatus)) return 'inactive';

  if (rawStatus === 'verification_under_review') {
    // TRUST THE DATABASE. This used to re-derive the stage from the EIGHT-field
    // completeness check and downgrade to 'verification_required' when the file
    // fell short — which was defensible while there was one bar, and became a
    // contradiction on 2026-09-18 when the two-bar model shipped.
    //
    // Under that model submit_supplier_docs legitimately sets this status on
    // the LIGHT bar (licence + contact + category), so the eight-field list
    // says "incomplete" about a supplier who has submitted everything stage 1
    // asks for. The result was a supplier told "We have everything we need" on
    // /supplier/verify and then shown a red "Verification required" banner on
    // the dashboard one click later.
    //
    // Only two RPCs can write this status, guard_profile_sensitive_fields
    // enforces that, and both check a confirmed identity before they do. So it
    // is a fact about what happened, not a guess to be second-guessed here.
    return 'verification_under_review';
  }

  if (LEGACY_REVIEW_STATUSES.includes(rawStatus)) {
    // Never auto-promote legacy statuses to under_review based on field completeness.
    // The only path to 'verification_under_review' is the explicit submit RPC, which
    // sets the canonical status in the DB. Legacy 'pending'/'under_review' etc. are
    // treated as verification_required so the supplier can complete the form and submit.
    if (emailConfirmed === false) return 'registered';
    return application.isApplicationComplete ? 'verification_required' : 'registered';
  }

  if (rawStatus === 'verification_required') {
    if (emailConfirmed === false) return 'registered';
    return application.isApplicationComplete ? 'verification_required' : 'registered';
  }

  if (rawStatus === 'registered' || LEGACY_REGISTERED_STATUSES.includes(rawStatus)) {
    if (emailConfirmed === false) return 'registered';
    return application.isApplicationComplete ? 'verification_required' : 'registered';
  }

  return emailConfirmed === false ? 'registered' : (application.isApplicationComplete ? 'verification_required' : 'registered');
}

export function getSupplierStageFromStatus(rawStatus) {
  const status = normalizeSupplierStatus(rawStatus);

  if (status === 'verified') return 'approved';
  if (status === 'rejected') return 'rejected';
  if (status === 'inactive') return 'inactive';
  if (status === 'verification_under_review') return 'under_review';

  return 'application';
}

export function getSupplierStageLabel(stage, lang = 'en') {
  return SUPPLIER_STAGE_LABELS[stage]?.[lang] || SUPPLIER_STAGE_LABELS[stage]?.en || stage;
}

export function isSupplierApproved(rawStatus) {
  return normalizeSupplierStatus(rawStatus) === 'verified';
}

export function isSupplierPubliclyVisible(rawStatus) {
  return isSupplierApproved(rawStatus);
}

// Whether the supplier's legal/verification documents are complete — i.e. they
// passed the 8-field submit_supplier_verification path and reached 'verified'.
// Since visibility no longer gates on this (registered suppliers are already
// vetted offline and are shown regardless), this flag now drives ONLY the
// "documents complete" badge, not whether the supplier appears. Same predicate
// as isSupplierPubliclyVisible, named for what it means post-visibility-change.
export function isSupplierDocsComplete(rawStatus) {
  return isSupplierApproved(rawStatus);
}

export function getSupplierMaabarId(profile = {}) {
  const rawValue = typeof profile === 'string' ? profile : profile?.maabar_supplier_id;
  const normalized = String(rawValue || '').trim().toUpperCase();
  return normalized || '';
}

export function getSupplierTradeLinks(profile = {}) {
  const tradeLinks = [];
  const addTradeLink = (value) => {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized || tradeLinks.includes(normalized)) return;
    tradeLinks.push(normalized);
  };

  if (Array.isArray(profile?.trade_links)) {
    profile.trade_links.forEach(addTradeLink);
  }

  addTradeLink(profile?.trade_link);
  return tradeLinks;
}

export function shouldNotifyAdminOfConfirmedSupplier(profile = {}, sessionUser = null) {
  if (profile?.role !== 'supplier' || !hasConfirmedEmail(sessionUser)) return false;
  return getSupplierResolvedStatus(profile, sessionUser) === 'verification_under_review';
}

export function buildSupplierTrustSignals(profile = {}) {
  const normalizedStatus = normalizeSupplierStatus(profile?.status);
  const trustSignals = [];

  if (normalizedStatus === 'verified') trustSignals.push('maabar_reviewed');
  // 'trade_profile_available' was REMOVED from this shared helper on
  // 2026-09-19. It is consumed by trader-facing surfaces, and a signal
  // meaning "this supplier has a storefront elsewhere" is the external
  // storefront rule leaking through a boolean. PendingApproval, which is
  // SUPPLIER-facing and reads the supplier's own row, derives it locally.
  if (profile?.wechat) trustSignals.push('wechat_available');
  if (profile?.whatsapp) trustSignals.push('whatsapp_available');
  if (Array.isArray(profile?.factory_images) && profile.factory_images.length > 0) trustSignals.push('factory_media_available');
  if (Number(profile?.years_experience) > 0) trustSignals.push('experience_declared');
  if (Number(profile?.reviews_count) > 0) trustSignals.push('review_history');
  if (Number(profile?.trust_score) > 0) trustSignals.push('trust_score');

  return trustSignals;
}

export function normalizeSupplierDocStoragePath(rawValue) {
  if (!rawValue || typeof rawValue !== 'string') return '';

  const trimmed = rawValue.trim();
  if (!trimmed) return '';

  if (!trimmed.startsWith('http')) {
    return trimmed.replace(/^supplier-docs\//, '');
  }

  try {
    const parsed = new URL(trimmed);
    const bucketMarker = '/supplier-docs/';
    const markerIndex = parsed.pathname.indexOf(bucketMarker);
    if (markerIndex === -1) return '';
    return decodeURIComponent(parsed.pathname.slice(markerIndex + bucketMarker.length));
  } catch {
    return '';
  }
}

export function getSupplierOnboardingState(profile = {}, sessionUser = null) {
  const application = getSupplierApplicationState(profile);
  const verification = getSupplierVerificationState(profile);
  const status = getSupplierResolvedStatus(profile, sessionUser);
  const emailConfirmed = sessionUser ? hasConfirmedEmail(sessionUser) : null;
  const stage = getSupplierStageFromStatus(status);
  const canAccessOperationalFeatures = status === 'verified';
  // A supplier can USE the platform the moment they register — verification no
  // longer gates Offers, Messaging, Requests or Products; it now gates only
  // PAYOUT (getting paid). So those features are unlocked for any active
  // (non-rejected/non-inactive) supplier. Buyers see a supplier's complete
  // (non-draft, is_active) products from day one: the verification->publish trigger
  // was dropped in 20260815000003 and the held-back rows republished.
  // canAccessOperationalFeatures now means specifically "verified / payout-capable"
  // and gates only Payout + Wallet.
  const canOperate = stage !== 'rejected' && stage !== 'inactive';
  const canAccessProducts = canOperate;
  const routeGuardRedirect = '/dashboard';

  return {
    ...verification,
    ...application,
    missingKeys: application.applicationMissingKeys,
    missingCount: application.applicationMissingCount,
    requiredCount: application.applicationRequiredCount,
    completedRequiredCount: application.applicationCompletedRequiredCount,
    progressPercent: application.applicationProgressPercent,
    rawStatus: profile?.status || 'registered',
    status,
    stage,
    stageLabel: getSupplierStageLabel(stage),
    trustSignals: buildSupplierTrustSignals({ ...profile, status }),
    tradeLinks: getSupplierTradeLinks(profile),
    emailConfirmed,
    maabarSupplierId: getSupplierMaabarId(profile),
    isRegisteredStatus: status === 'registered',
    isVerificationRequiredStatus: status === 'verification_required',
    isVerificationUnderReviewStatus: status === 'verification_under_review',
    isVerifiedStatus: status === 'verified',
    isApplicationStage: stage === 'application',
    isUnderReviewStage: stage === 'under_review' || status === 'verification_under_review',
    isApprovedStage: stage === 'approved',
    isRejectedStage: stage === 'rejected',
    isInactiveStage: stage === 'inactive',
    canAccessOperationalFeatures,
    canAccessMessaging: canOperate,
    canAccessRequests: canOperate,
    canAccessProducts,
    canAccessOffers: canOperate,
    canAccessPayoutSetup: canAccessOperationalFeatures,
    routeGuardRedirect,
    // Before verification a supplier can reach everything EXCEPT Payout + Wallet
    // (those involve getting paid, which stays gated). Anything not listed here is
    // shown locked in the dashboard until the supplier is verified.
    limitedTabs: canOperate
      ? ['overview', 'verification', 'settings', 'managed-matches', 'requests', 'direct-orders', 'my-products', 'add-product', 'offers', 'samples', 'product-inquiries', 'reviews', 'messages']
      : ['overview', 'verification', 'settings'],
    fullTabs: ['overview', 'verification', 'payout', 'requests', 'my-products', 'offers', 'add-product', 'samples', 'product-inquiries', 'reviews', 'messages', 'settings'],
  };
}

export function shouldPromoteSupplierAfterEmailConfirmation(profile = {}, sessionUser = null) {
  if (profile?.role !== 'supplier' || !hasConfirmedEmail(sessionUser)) return false;

  const rawStatus = normalizeRawSupplierStatus(profile?.status);
  const { isApplicationComplete } = getSupplierApplicationState(profile);

  return isApplicationComplete && (rawStatus === 'registered' || LEGACY_REGISTERED_STATUSES.includes(rawStatus));
}

export function getSupplierPrimaryRoute(profile = {}, sessionUser = null) {
  return getSupplierOnboardingState(profile, sessionUser).routeGuardRedirect;
}
